import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Check, X, Eye, Clock, CheckCircle, XCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useCountry } from '../context/CountryContext';

const PaymentVerificationDashboard = () => {
  const { formatCurrency } = useCountry();
  const [payments, setPayments] = useState([]);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    // Mock data - replace with actual API call
    const mockPayments = [
      {
        id: 1,
        orderId: 'ORD-001',
        listingTitle: 'iPhone 15 Pro Max',
        buyerName: 'Kasun Perera',
        senderName: 'K. Perera',
        amount: 450000,
        paymentMethod: 'bank_transfer',
        transactionId: 'TXN123456',
        paymentProof: 'https://via.placeholder.com/400x600/cccccc/000000?text=Bank+Receipt',
        status: 'pending',
        submittedAt: '2025-07-27 10:30 AM',
        notes: 'Transferred from Commercial Bank'
      },
      {
        id: 2,
        orderId: 'ORD-002',
        listingTitle: 'Toyota Aqua 2020',
        buyerName: 'Nimal Silva',
        senderName: 'N. Silva',
        amount: 5200000,
        paymentMethod: 'mobile_money',
        transactionId: 'EZ987654',
        paymentProof: 'https://via.placeholder.com/400x600/cccccc/000000?text=eZCash+Screenshot',
        status: 'pending',
        submittedAt: '2025-07-27 11:15 AM',
        notes: 'Paid via eZ Cash'
      },
      {
        id: 3,
        orderId: 'ORD-003',
        listingTitle: 'MacBook Pro M3',
        buyerName: 'Amara Fernando',
        senderName: 'Amara F',
        amount: 680000,
        paymentMethod: 'bank_transfer',
        transactionId: 'REF789012',
        paymentProof: 'https://via.placeholder.com/400x600/cccccc/000000?text=Bank+Slip',
        status: 'verified',
        submittedAt: '2025-07-26 03:20 PM',
        verifiedAt: '2025-07-26 05:45 PM',
        notes: ''
      }
    ];
    setPayments(mockPayments);
  }, []);

  const handleVerify = (paymentId) => {
    // Mock verification - replace with actual API call
    setPayments(payments.map(p => 
      p.id === paymentId 
        ? { ...p, status: 'verified', verifiedAt: new Date().toLocaleString() }
        : p
    ));
    setShowPreview(false);
    alert('Payment verified! Order will be processed.');
  };

  const handleReject = (paymentId) => {
    const reason = prompt('Enter rejection reason:');
    if (reason) {
      setPayments(payments.map(p => 
        p.id === paymentId 
          ? { ...p, status: 'rejected', rejectionReason: reason }
          : p
      ));
      setShowPreview(false);
      alert('Payment rejected. Buyer will be notified.');
    }
  };

  const handlePreview = (payment) => {
    setSelectedPayment(payment);
    setShowPreview(true);
  };

  const pendingPayments = payments.filter(p => p.status === 'pending');
  const verifiedPayments = payments.filter(p => p.status === 'verified');
  const rejectedPayments = payments.filter(p => p.status === 'rejected');

  const PaymentCard = ({ payment }) => (
    <Card className="mb-4 hover:shadow-lg transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              <Badge variant="outline">{payment.orderId}</Badge>
              <Badge variant={
                payment.paymentMethod === 'bank_transfer' ? 'default' : 'secondary'
              }>
                {payment.paymentMethod === 'bank_transfer' ? 'Bank Transfer' : 'Mobile Money'}
              </Badge>
            </div>
            <h3 className="font-semibold text-lg mb-1">{payment.listingTitle}</h3>
            <p className="text-sm text-gray-600">Buyer: {payment.buyerName}</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-blue-600">{formatCurrency(payment.amount)}</p>
            {payment.status === 'pending' && (
              <Badge className="mt-2 bg-yellow-500">
                <Clock className="h-3 w-3 mr-1" />
                Pending
              </Badge>
            )}
            {payment.status === 'verified' && (
              <Badge className="mt-2 bg-green-500">
                <CheckCircle className="h-3 w-3 mr-1" />
                Verified
              </Badge>
            )}
            {payment.status === 'rejected' && (
              <Badge className="mt-2 bg-red-500">
                <XCircle className="h-3 w-3 mr-1" />
                Rejected
              </Badge>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
          <div>
            <span className="text-gray-600">Sender Name:</span>
            <p className="font-medium">{payment.senderName}</p>
          </div>
          <div>
            <span className="text-gray-600">Transaction ID:</span>
            <p className="font-medium">{payment.transactionId || 'N/A'}</p>
          </div>
          <div>
            <span className="text-gray-600">Submitted:</span>
            <p className="font-medium">{payment.submittedAt}</p>
          </div>
          {payment.verifiedAt && (
            <div>
              <span className="text-gray-600">Verified:</span>
              <p className="font-medium">{payment.verifiedAt}</p>
            </div>
          )}
        </div>

        {payment.notes && (
          <div className="mb-4 p-3 bg-gray-50 rounded text-sm">
            <span className="text-gray-600">Notes: </span>
            <span>{payment.notes}</span>
          </div>
        )}

        <div className="flex space-x-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => handlePreview(payment)}
            className="flex-1"
          >
            <Eye className="h-4 w-4 mr-2" />
            View Proof
          </Button>
          {payment.status === 'pending' && (
            <>
              <Button
                size="sm"
                onClick={() => handleVerify(payment.id)}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                <Check className="h-4 w-4 mr-2" />
                Verify
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => handleReject(payment.id)}
                className="flex-1"
              >
                <X className="h-4 w-4 mr-2" />
                Reject
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-8">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-3xl font-bold mb-2">Payment Verification</h1>
          <p className="text-blue-100">Review and approve payment proofs</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Pending Verification</p>
                  <p className="text-3xl font-bold text-yellow-600">{pendingPayments.length}</p>
                </div>
                <Clock className="h-12 w-12 text-yellow-600 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Verified Today</p>
                  <p className="text-3xl font-bold text-green-600">{verifiedPayments.length}</p>
                </div>
                <CheckCircle className="h-12 w-12 text-green-600 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Amount Pending</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {formatCurrency(pendingPayments.reduce((sum, p) => sum + p.amount, 0))}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Payments List */}
        <Card>
          <CardHeader>
            <CardTitle>Payment Submissions</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="pending">
              <TabsList className="mb-6">
                <TabsTrigger value="pending">
                  Pending ({pendingPayments.length})
                </TabsTrigger>
                <TabsTrigger value="verified">
                  Verified ({verifiedPayments.length})
                </TabsTrigger>
                <TabsTrigger value="rejected">
                  Rejected ({rejectedPayments.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="pending">
                {pendingPayments.length > 0 ? (
                  pendingPayments.map(payment => (
                    <PaymentCard key={payment.id} payment={payment} />
                  ))
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <Clock className="h-16 w-16 mx-auto mb-4 opacity-50" />
                    <p>No pending payments</p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="verified">
                {verifiedPayments.length > 0 ? (
                  verifiedPayments.map(payment => (
                    <PaymentCard key={payment.id} payment={payment} />
                  ))
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <p>No verified payments yet</p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="rejected">
                {rejectedPayments.length > 0 ? (
                  rejectedPayments.map(payment => (
                    <PaymentCard key={payment.id} payment={payment} />
                  ))
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <p>No rejected payments</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Payment Preview Dialog */}
      {showPreview && selectedPayment && (
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Payment Proof - {selectedPayment.orderId}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Order:</p>
                  <p className="font-semibold">{selectedPayment.listingTitle}</p>
                </div>
                <div>
                  <p className="text-gray-600">Amount:</p>
                  <p className="font-semibold text-blue-600">{formatCurrency(selectedPayment.amount)}</p>
                </div>
                <div>
                  <p className="text-gray-600">Buyer:</p>
                  <p className="font-semibold">{selectedPayment.buyerName}</p>
                </div>
                <div>
                  <p className="text-gray-600">Sender:</p>
                  <p className="font-semibold">{selectedPayment.senderName}</p>
                </div>
              </div>

              <div className="border rounded-lg p-4 bg-gray-50">
                <img 
                  src={selectedPayment.paymentProof} 
                  alt="Payment proof" 
                  className="max-w-full mx-auto"
                />
              </div>

              {selectedPayment.status === 'pending' && (
                <div className="flex space-x-2">
                  <Button
                    onClick={() => handleVerify(selectedPayment.id)}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    <Check className="h-4 w-4 mr-2" />
                    Verify Payment
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => handleReject(selectedPayment.id)}
                    className="flex-1"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Reject
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default PaymentVerificationDashboard;
